import { Button, FormContentIsland, Grid, HookForm, Skeleton } from '@razz-team/ui-web';
import { useTranslation } from 'react-i18next';
import { useCardQuery, useCardUpdateSpendingLimitMutation } from '../../../api/cards';
import { useCallback, useEffect } from 'react';
import { Route } from '../../../routes/account.cards_.$cardId_.limits';
import { MainLayout } from '../../Layout/MainLayout';
import { MainBreadcrumbs } from '../../Layout/MainBreadcrumbs';
import { useForm } from 'react-hook-form';
import { useFormError } from '../../../hooks/useFormError';
import { AmountField } from '../../Field/AmountField';
import {
  mapClientMoneyToServerMoney,
  parseFloatValue,
  serverAmountToClientAmount,
} from '@razz-team/utils';

type CardLimitsFormValues = {
  clientAmount: string;
};

export function CardLimitsPage() {
  const { t } = useTranslation();
  const { cardId } = Route.useParams();
  const cardQuery = useCardQuery({ cardId });
  const mutation = useCardUpdateSpendingLimitMutation(cardId);
  const navigate = Route.useNavigate();

  const currency = cardQuery.data?.spendingLimitPolicy.limit?.currency ?? 'EUR';

  const form = useForm<CardLimitsFormValues>({
    defaultValues: {
      clientAmount: '10000',
    },
  });

  useFormError(form, 'clientAmount', mutation.error);

  useEffect(() => {
    if (mutation.isSuccess) {
      navigate({ to: `/account/cards/${cardId}`, params: { cardId } });
    }
  }, [mutation.isSuccess, navigate, cardId]);

  useEffect(() => {
    const policy = cardQuery.data?.spendingLimitPolicy;
    if (policy) {
      form.reset({
        clientAmount: serverAmountToClientAmount(policy.limit).toString(),
      });
    }
  }, [cardQuery.data, form]);

  const handleSubmit = useCallback(
    (values: CardLimitsFormValues) => {
      mutation.mutate({
        name: 'MONTHLY',
        limit: mapClientMoneyToServerMoney({
          currency,
          amount: parseFloatValue(values.clientAmount),
        }),
      });
    },
    [mutation, currency],
  );

  return (
    <HookForm form={form} onSubmit={handleSubmit}>
      <MainLayout
        title={
          <MainBreadcrumbs
            title={t('Card limits')}
            parent={{
              to: '/account/cards/$cardId',
              params: { cardId },
              title: cardQuery.data?.name,
              loading: cardQuery.isPending,
            }}
          />
        }
        compact
      >
        <Grid size={12}>
          <FormContentIsland title={t('Update limits')}>
            {cardQuery.isPending ? (
              <>
                <Skeleton variant="rounded" height={53} />
              </>
            ) : (
              <>
                <AmountField
                  name="clientAmount"
                  label={t('Monthly limit')}
                  currency={currency}
                  required
                  autoFocus
                />
              </>
            )}
          </FormContentIsland>
        </Grid>
        <Grid size={12}>
          <Button
            type="submit"
            size="large"
            color="primary"
            loading={mutation.isPending}
            disabled={!form.formState.isDirty}
            fullWidth
          >
            {t('Update')}
          </Button>
        </Grid>
      </MainLayout>
    </HookForm>
  );
}
